import { trackProductEvent, type ProductEventName } from "@/lib/productAnalytics";

/**
 * Lightweight device/browser detection + analytics helper.
 *
 * Product events reuse the existing GA4 setup and pass through the central
 * consent/privacy gate in `productAnalytics.ts`.
 */

export function getUA(): string {
  return typeof navigator !== "undefined" ? navigator.userAgent : "";
}

/** iPhone, iPod, or iPad (including iPadOS which reports as Macintosh + touch). */
export function isIOSDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = getUA();
  if (/iphone|ipod|ipad/i.test(ua)) return true;
  // iPadOS 13+ desktop-class Safari
  return /macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
}

/**
 * True Safari on iOS. On iOS every browser uses WebKit, but only Safari itself
 * exposes the Share → "Add to Home Screen" flow.
 */
export function isIOSSafari(): boolean {
  if (!isIOSDevice()) return false;
  const ua = getUA();
  const otherBrowser = /crios|fxios|edgios|opios|duckduckgo|yabrowser|brave/i.test(ua);
  const inApp = isInAppBrowser();
  return !otherBrowser && !inApp && /safari/i.test(ua);
}

/** Embedded webviews (Instagram, Facebook, Google app, LinkedIn, TikTok, ...). */
export function isInAppBrowser(): boolean {
  const ua = getUA();
  return /fban|fbav|fb_iab|instagram|line\/|linkedinapp|micromessenger|tiktok|pinterest|snapchat|gsa\//i.test(ua);
}

export function isAndroidDevice(): boolean {
  return /android/i.test(getUA());
}

export type Platform = "ios" | "android" | "desktop";

export function getPlatform(): Platform {
  if (isIOSDevice()) return "ios";
  if (isAndroidDevice()) return "android";
  return "desktop";
}

/** Fire-and-forget product event through the consent-safe GA4 wrapper. */
export function trackEvent(name: ProductEventName, params: Record<string, unknown> = {}) {
  return trackProductEvent(name, params);
}
